/**
 * Client-side image compression utility
 * Resizes images exceeding maxDimension and compresses them using HTML Canvas.
 */
export function compressImage(file, maxDimension = 1200, quality = 0.75) {
  return new Promise((resolve) => {
    // If it's not a compressable image, return the original file immediately
    if (!file || !file.type || !file.type.startsWith('image/')) {
      return resolve(file);
    }
    
    // Skip compression for GIFs (since canvas drawing would flatten the animation)
    if (file.type === 'image/gif') {
      return resolve(file);
    }

    const reader = new FileReader();
    reader.readAsDataURL(file);
    
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      
      img.onload = () => {
        let width = img.width;
        let height = img.height;

        // Skip resizing if the image is already within maxDimension
        if (width <= maxDimension && height <= maxDimension) {
          // But still re-export through canvas to compress the file size
          // unless the file is already very small (e.g., < 200 KB)
          if (file.size < 200 * 1024) {
            return resolve(file);
          }
        } else {
          // Calculate new dimensions preserving the aspect ratio
          if (width > height) {
            height = Math.round((height * maxDimension) / width);
            width = maxDimension;
          } else {
            width = Math.round((width * maxDimension) / height);
            height = maxDimension;
          }
        }

        try {
          const canvas = document.createElement('canvas');
          canvas.width = width;
          canvas.height = height;

          const ctx = canvas.getContext('2d');
          
          // Draw image on canvas (handles orientation and resizing)
          ctx.drawImage(img, 0, 0, width, height);

          // Convert canvas content to compressed JPEG Blob
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                return resolve(file); // Fallback to original
              }
              
              // If the compressed blob is somehow larger than the original file, use the original
              if (blob.size >= file.size) {
                return resolve(file);
              }

              // Create a new file with the compressed blob data
              const compressedFile = new File([blob], file.name.replace(/\.[^/.]+$/, "") + ".jpg", {
                type: 'image/jpeg',
                lastModified: Date.now(),
              });
              
              resolve(compressedFile);
            },
            'image/jpeg',
            quality
          );
        } catch (err) {
          console.error('Canvas compression error, falling back to original:', err);
          resolve(file);
        }
      };
      
      img.onerror = (err) => {
        console.error('Image load error during compression, falling back to original:', err);
        resolve(file);
      };
    };
    
    reader.onerror = (err) => {
      console.error('FileReader error during compression, falling back to original:', err);
      resolve(file);
    };
  });
}
